import React, { HTMLProps, ReactElement } from 'react';
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
} from 'mui-datatables';
import { createTheme, useTheme } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider } from '@mui/material';
import MfLinearSimpleProgress from '../Progress/MfLinearSimpleProgress';
import style from './MfResponsiveTable.module.scss';
import mfTheme from '../../../themes/MfTheme';

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

export const StringCellHeadWrapper = ({
  children,
  ...rest
}: HTMLProps<HTMLDivElement>) => (
  <div {...rest} className={style.stringCellHeadWrapper}>
    {children}
  </div>
);

export const NumericCellHeadWrapper = ({
  children,
  ...rest
}: HTMLProps<HTMLDivElement>) => (
  <div {...rest} className={style.numericCellHeadWrapper}>
    {children}
  </div>
);

export const NumericCellValue = ({
  children,
  ...rest
}: HTMLProps<HTMLDivElement>) => (
  <div {...rest} className={style.numericCellValue}>
    {children}
  </div>
);

export const StringCellValue = ({
  children,
  ...rest
}: HTMLProps<HTMLDivElement>) => (
  <div {...rest} className={style.stringCellValue}>
    {children}
  </div>
);

const defaultOptions: MUIDataTableOptions = {
  responsive: 'standard',
  pagination: false,
  disableToolbarSelect: true,
  selectableRows: 'none',
  elevation: 0,
  sort: false,
  filter: false,
  search: false,
  print: false,
  download: false,
  viewColumns: false,
  selectableRowsOnClick: false,
  textLabels: {
    body: {
      noMatch: 'Нет записей',
    },
    pagination: {
      rowsPerPage: 'Количество заявок на странице',
    },
  },
};

interface Props {
  data: (Record<string, unknown> | string | ReactElement | any)[][];
  columns: MUIDataTableColumnDef[];
  options?: MUIDataTableOptions;
  rowBackground?: string;
  loading?: boolean;
}

declare module '@mui/material/styles' {
  interface Components {
    [key: string]: unknown;
  }
}

const MfResponsiveTable = ({
  data,
  columns,
  options,
  rowBackground = 'none',
  loading,
}: Props) => {
  const theme = useTheme();
  const getMuiTheme = () =>
    createTheme({
      ...mfTheme,
      components: {
        MUIDataTableToolbar: {
          root: {
            background: 'red',
            display: 'none !important',
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
            [theme.breakpoints.down('md')]: {
              display: 'none',
            },
          },
        },
        MUIDataTableSelectCell: {
          root: {
            [theme.breakpoints.down('lg')]: {
              border: 'none',
              height: 'auto !important',
              padding: '4px 4px 4px 0px',
            },
          },
        },
        MUIDataTableBodyCell: {
          root: {
            [theme.breakpoints.down('lg')]: {
              border: 'none',
              height: 'auto !important',
              padding: '4px',
            },
          },
          stackedCommon: {
            [theme.breakpoints.down('lg')]: {
              fontSize: '13px',
            },
          },
        },
        MUIDataTableBodyRow: {
          root: {
            [theme.breakpoints.down('lg')]: {
              border: 'none !important',
              borderBottom: '1px dashed rgba(0, 0, 0, 0.2) !important',
            },
          },
          responsiveStacked: {
            padding: '10px 0',
          },
        },
        MUIDataTable: {
          tableRoot: {
            background: rowBackground,
          },
        },
        MUIDataTableHeadCell: {
          fixedHeader: {
            background: rowBackground,
            backgroundColor: rowBackground,
          },
        },
      },
    });

  return (
    <div className={style.tableWrapper}>
      <CacheProvider value={muiCache}>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title=""
            columns={columns}
            options={{ ...defaultOptions, ...options }}
            data={data}
          />
        </ThemeProvider>
      </CacheProvider>
      {loading && (
        <div className={style.tableOverlay}>
          <MfLinearSimpleProgress />
        </div>
      )}
    </div>
  );
};

MfResponsiveTable.defaultProps = {
  options: {},
};

export default MfResponsiveTable;
